import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { DataGrid } from '../../components/ui/table';
import { Alert, AlertDescription } from '../../components/ui/alert';
import SearchableSelect from '../../components/shared/SearchableSelect';
import { GridSearchInput } from '../../components/shared/GridToolbar';
import { ConsoleActionButton, ListConsolePage } from '../../components/shared/ListConsoleLayout';
import '../transaction-monitoring/TransactionMonitoring.css';
import './Permissions.css';
import {
  fetchPermissionUsers,
  fetchUserPermissions,
  saveUserPermissions,
  type PermissionUser,
  type PermissionModuleRow,
} from './api';
import { buildMatrixColumns, capKeysForModuleRow, type CapKey } from './capabilities';
import {
  createPermissionMatrixColumnDefs,
  PERMISSIONS_GRID_DEFAULT_COL_DEF,
  permissionMatrixGridOptions,
  type PermissionsGridContext,
} from './table/columnDefs';

function userKey(u: Pick<PermissionUser, 'userId' | 'userType'>) {
  return `${u.userType}:${u.userId}`;
}

function applyCapToggle(row: PermissionModuleRow, action: CapKey, value: boolean): PermissionModuleRow {
  const next = { ...row, [action]: value };
  if (action === 'canView' && !value) {
    next.canCreate = next.canUpdate = next.canDelete = next.canExport = false;
  } else if (action !== 'canView' && value) {
    next.canView = true;
  }
  return next;
}

export default function Permissions() {
  const [users, setUsers] = useState<PermissionUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [rows, setRows] = useState<PermissionModuleRow[]>([]);
  const [moduleSearch, setModuleSearch] = useState('');
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixError, setMatrixError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const selected = useMemo(
    () => users.find((u) => userKey(u) === selectedKey) ?? null,
    [users, selectedKey],
  );

  const matrixColumns = useMemo(
    () => buildMatrixColumns(rows.map((r) => r.moduleCode)),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const q = moduleSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = String(r.moduleName ?? '').toLowerCase();
      const code = String(r.moduleCode ?? '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [rows, moduleSearch]);

  useEffect(() => {
    let active = true;
    (async () => {
      setUsersLoading(true);
      setPageError(null);
      try {
        const data = await fetchPermissionUsers();
        if (active) setUsers(data);
      } catch (err) {
        if (active) setPageError((err as Error).message || 'Failed to load users.');
      } finally {
        if (active) setUsersLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const ADMINS_BUCKET = '__ADMINS__';
  const merchantOptions = useMemo(() => {
    const byId = new Map<string, string>();
    users.forEach((u) => {
      if (u.userType === 'MERCHANT' && u.merchantId) {
        byId.set(String(u.merchantId), u.merchantName || `Merchant ${u.merchantId}`);
      }
    });
    const opts = [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const hasAdmins = users.some((u) => u.userType === 'ADMIN');
    return hasAdmins ? [{ value: ADMINS_BUCKET, label: 'Platform Admins' }, ...opts] : opts;
  }, [users]);

  const userOptions = useMemo(() => {
    if (!selectedMerchant) return [];
    const list =
      selectedMerchant === ADMINS_BUCKET
        ? users.filter((u) => u.userType === 'ADMIN')
        : users.filter((u) => u.userType === 'MERCHANT' && String(u.merchantId) === selectedMerchant);
    return list.map((u) => ({ value: userKey(u), label: `${u.name} · ${u.email}` }));
  }, [users, selectedMerchant]);

  const resetMatrixState = () => {
    setSelectedKey('');
    setRows([]);
    setModuleSearch('');
    setMatrixError(null);
    setSaveMsg(null);
    setDirty(false);
  };

  const onPickMerchant = (value: string) => {
    setSelectedMerchant(value);
    resetMatrixState();
  };

  const loadMatrix = async (u: PermissionUser) => {
    setSaveMsg(null);
    setMatrixError(null);
    setDirty(false);
    setMatrixLoading(true);
    try {
      const matrix = await fetchUserPermissions(u.userId, u.userType);
      setRows(matrix.modules);
      setModuleSearch('');
    } catch (err) {
      setRows([]);
      setMatrixError((err as Error).message || 'Failed to load permissions.');
    } finally {
      setMatrixLoading(false);
    }
  };

  const onPickUser = (key: string) => {
    setSelectedKey(key);
    const u = users.find((x) => userKey(x) === key);
    if (!u) return;
    if (u.isSuperAdmin) {
      setRows([]);
      setModuleSearch('');
      setMatrixError(null);
      setSaveMsg(null);
      setDirty(false);
      return;
    }
    void loadMatrix(u);
  };

  const toggle = useCallback((moduleId: number, action: CapKey, value: boolean) => {
    setSaveMsg(null);
    setDirty(true);
    setRows((prev) =>
      prev.map((r) => (r.moduleId !== moduleId ? r : applyCapToggle(r, action, value))),
    );
  }, []);

  const toggleAllowAll = useCallback(
    (moduleId: number, enable: boolean) => {
      setSaveMsg(null);
      setDirty(true);
      setRows((prev) =>
        prev.map((r) => {
          if (r.moduleId !== moduleId) return r;
          let next = { ...r };
          const keys = capKeysForModuleRow(r.moduleCode, matrixColumns);
          if (enable) {
            for (const key of keys) {
              next = applyCapToggle(next, key, true);
            }
          } else {
            for (const key of keys) {
              next[key] = false;
            }
          }
          return next;
        }),
      );
    },
    [matrixColumns],
  );

  const permissionColumnDefs = useMemo(
    () => createPermissionMatrixColumnDefs(matrixColumns, filteredRows),
    [matrixColumns, filteredRows],
  );

  const permissionGridContext = useMemo<PermissionsGridContext>(
    () => ({
      matrixColumns,
      onToggle: toggle,
      onToggleAllowAll: toggleAllowAll,
    }),
    [matrixColumns, toggle, toggleAllowAll],
  );

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveMsg(null);
    setMatrixError(null);
    try {
      await saveUserPermissions({
        userId: selected.userId,
        userType: selected.userType,
        modules: rows.map((r) => ({
          moduleId: r.moduleId,
          canView: r.canView,
          canCreate: r.canCreate,
          canUpdate: r.canUpdate,
          canDelete: r.canDelete,
          canExport: r.canExport,
        })),
      });
      setSaveMsg('Saved. This user will be signed out and must log in again.');
      setDirty(false);
      await loadMatrix(selected);
    } catch (err) {
      const backendMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setMatrixError(backendMsg || (err as Error).message || 'Failed to save permissions.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ListConsolePage title="Permissions" error={pageError}>
      <div className="dh-table-section tm-table-sections">
      <Card className="tm-table-card tm-table-toolbar-card">
        <div className="tm-table-panel">
          <div className="tm-table-toolbar-row">
            <div className="perm-toolbar-fields">
              <div className="perm-toolbar-field">
                <span className="perm-field-label">Merchant</span>
                <SearchableSelect
                  value={selectedMerchant}
                  options={merchantOptions}
                  onChange={onPickMerchant}
                  placeholder={usersLoading ? 'Loading…' : 'Select a merchant…'}
                  searchPlaceholder="Search merchant…"
                  disabled={usersLoading}
                  aria-label="Select merchant"
                />
              </div>
              <div className="perm-toolbar-field">
                <span className="perm-field-label">User</span>
                <SearchableSelect
                  value={selectedKey}
                  options={userOptions}
                  onChange={onPickUser}
                  placeholder={!selectedMerchant ? 'Select a merchant first' : 'Select a user…'}
                  searchPlaceholder="Search name or email…"
                  disabled={usersLoading || !selectedMerchant}
                  aria-label="Select user"
                />
              </div>
              <div className="perm-toolbar-field perm-toolbar-field-search">
                <span className="perm-field-label">Modules</span>
                <GridSearchInput
                  value={moduleSearch}
                  onValueChange={setModuleSearch}
                  placeholder="Search modules…"
                  inputClassName="text-xs font-normal tm-toolbar-search-input"
                  disabled={
                    usersLoading ||
                    !selected ||
                    selected.isSuperAdmin ||
                    matrixLoading ||
                    rows.length === 0
                  }
                  aria-label="Search modules"
                />
              </div>
            </div>
            {selected && !selected.isSuperAdmin && (
              <div className="tm-table-toolbar-actions">
                <div className="tm-toolbar-controls">
                  <ConsoleActionButton
                    variant="outline"
                    onClick={() => void loadMatrix(selected)}
                    disabled={saving || matrixLoading || !dirty}
                  >
                    Revert
                  </ConsoleActionButton>
                  <ConsoleActionButton
                    onClick={() => void save()}
                    disabled={saving || matrixLoading || rows.length === 0 || !dirty}
                  >
                    {saving && <Loader2 className="animate-spin" size={14} />}
                    Save changes
                  </ConsoleActionButton>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="tm-table-card tm-table-grid-card overflow-hidden">

        {!selected ? (
          <div className="perm-empty">
            <p className="perm-empty-title">No user selected</p>
            <p className="perm-empty-sub">
              Pick a merchant, then a user, to review and edit their access.
            </p>
          </div>
        ) : selected.isSuperAdmin ? (
          <div className="p-4">
            <Alert variant="destructive">
              <AlertDescription>
                Super admin permissions cannot be edited — a Super Admin always has full access.
              </AlertDescription>
            </Alert>
          </div>
        ) : matrixLoading ? (
          <div className="perm-state-row perm-state-muted">
            <Loader2 className="animate-spin" size={16} aria-hidden />
            Loading permissions…
          </div>
        ) : matrixError ? (
          <div className="p-4">
            <Alert variant="destructive">
              <AlertDescription>{matrixError}</AlertDescription>
            </Alert>
          </div>
        ) : rows.length === 0 ? (
          <div className="perm-state-row perm-state-muted">No modules available.</div>
        ) : (
          <div className="perm-matrix-body">
            {saveMsg && (
              <Alert className="perm-matrix-alert">
                <AlertDescription>{saveMsg}</AlertDescription>
              </Alert>
            )}
            {filteredRows.length === 0 ? (
              <div className="perm-state-row perm-state-muted">No modules match your search.</div>
            ) : (
              <DataGrid<PermissionModuleRow>
                rowData={filteredRows}
                columnDefs={permissionColumnDefs}
                context={permissionGridContext}
                defaultColDef={PERMISSIONS_GRID_DEFAULT_COL_DEF}
                gridOptions={permissionMatrixGridOptions}
                includeAuditColumns={false}
                height="100%"
                className="tm-ag-grid perm-ag-grid"
              />
            )}
          </div>
        )}
      </Card>
      </div>
    </ListConsolePage>
  );
}
